/**
 * Bootstrapper.tsx
 * ----------------
 * Loads preloaded data into the application state.
 */

import * as React from 'react';
import { useDispatch } from 'react-redux';

import { Action } from '../../actions/types';

interface BootstrapperProps {
    errors: { action: (blob: unknown) => Action; name: string }[];
    preloads: { action: (blob: unknown) => Action; name: string }[];
}

export const Bootstrapper: React.FunctionComponent<BootstrapperProps> = props => {
    // Action hooks
    const dispatch = useDispatch();

    // Component state hooks
    const [isPreloaded, setIsPreloaded] = React.useState<boolean>(false);

    const { children, errors, preloads } = props;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    // Justification: It's more straightforward to use explicit any for window, as this is used sparingly on startup.
    const windowObj: any = window as any;
    /* eslint-enable @typescript-eslint/no-explicit-any */

    // If there are any errors, log them all and throw the first one
    if (!isPreloaded && errors.length > 0) {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        // Justification: It's more straightforward to use explicit any for window, as this is used sparingly on
        // startup.
        let firstError: any = undefined;
        /* eslint-enable @typescript-eslint/no-explicit-any */

        errors.forEach(error => {
            const { action, name } = error;
            const errorBlob = windowObj[name];

            if (firstError !== undefined) {
                firstError = errorBlob;
                console.error(errorBlob);
                dispatch(action(errorBlob));
            }
        });

        if (firstError !== undefined) {
            throw new Error('Error(s) occurred on view bootstrapping. Check console for further details.');
        }
    }

    // If there are any preloads, fire all the actions
    if (!isPreloaded && preloads.length > 0) {
        preloads.forEach(preload => {
            const { action, name } = preload;

            if (windowObj[name] !== undefined) {
                dispatch(action(windowObj[name]));
            }
        });
    }

    // Mark preload as complete
    if (!isPreloaded) {
        setIsPreloaded(true);
    }

    return <div>{children}</div>;
};
